import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const GuestCard = ({ guest, onEdit, onDelete, onViewHistory }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getDietaryChipColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'allergy':
        return 'bg-error text-error-foreground';
      case 'intolerance':
        return 'bg-warning text-warning-foreground';
      case 'preference':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatLastVisit = (date) => {
    const visitDate = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - visitDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-warm hover:shadow-warm-md transition-warm">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Image
                src={guest?.avatar}
                alt={guest?.avatarAlt}
                className="w-12 h-12 rounded-full object-cover"
              />
              {guest?.familyGroup && (
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="Users" size={12} color="white" />
                </div>
              )}
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{guest?.name}</h3>
              <p className="text-sm text-muted-foreground">{guest?.email}</p>
              {guest?.familyGroup && (
                <p className="text-xs text-primary font-medium">{guest?.familyGroup}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(guest)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="Edit2" size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
            </Button>
          </div>
        </div>

        {/* Dietary Restrictions */}
        {guest?.dietaryRestrictions && guest?.dietaryRestrictions?.length > 0 && (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {guest?.dietaryRestrictions?.slice(0, 3)?.map((restriction, index) => (
                <span
                  key={index}
                  className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getDietaryChipColor(restriction?.type)}`}
                >
                  {restriction?.type === 'allergy' && <Icon name="AlertTriangle" size={10} />}
                  {restriction?.type === 'intolerance' && <Icon name="AlertCircle" size={10} />}
                  {restriction?.type === 'preference' && <Icon name="Heart" size={10} />}
                  <span>{restriction?.name}</span>
                </span>
              ))}
              {guest?.dietaryRestrictions?.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                  +{guest?.dietaryRestrictions?.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center space-x-4">
            <span className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} />
              <span>{guest?.totalVisits} visits</span>
            </span>
            {guest?.lastVisit && (
              <span className="flex items-center space-x-1">
                <Icon name="Clock" size={14} />
                <span>{formatLastVisit(guest?.lastVisit)}</span>
              </span>
            )}
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewHistory(guest)}
            className="text-primary hover:text-primary/80"
          >
            View History
          </Button>
        </div>
      </div>
      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-border p-4 bg-muted/30">
          <div className="space-y-4">
            {/* Full Dietary Profile */}
            {guest?.dietaryRestrictions && guest?.dietaryRestrictions?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Dietary Profile</h4>
                <div className="space-y-2">
                  {guest?.dietaryRestrictions?.map((restriction, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-card rounded-md">
                      <div className="flex items-center space-x-2">
                        <span className={`w-2 h-2 rounded-full ${
                          restriction?.type === 'allergy' ? 'bg-error' :
                          restriction?.type === 'intolerance' ? 'bg-warning' : 'bg-secondary'
                        }`} />
                        <span className="text-sm font-medium">{restriction?.name}</span>
                        <span className="text-xs text-muted-foreground capitalize">({restriction?.type})</span>
                      </div>
                      {restriction?.severity && (
                        <span className="text-xs px-2 py-1 bg-muted rounded-full">
                          {restriction?.severity}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Visits */}
            {guest?.recentVisits && guest?.recentVisits?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Recent Visits</h4>
                <div className="space-y-2">
                  {guest?.recentVisits?.slice(0, 3)?.map((visit, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-card rounded-md">
                      <div>
                        <p className="text-sm font-medium">{visit?.eventName}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(visit.date)?.toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">
                          {visit?.dishesServed} dishes served
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Info */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Contact Information</h4>
              <div className="space-y-1">
                <div className="flex items-center space-x-2 text-sm">
                  <Icon name="Mail" size={14} />
                  <span>{guest?.email}</span>
                </div>
                {guest?.phone && (
                  <div className="flex items-center space-x-2 text-sm">
                    <Icon name="Phone" size={14} />
                    <span>{guest?.phone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-2 pt-2 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(guest)}
                iconName="Edit2"
                iconPosition="left"
              >
                Edit Profile
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewHistory(guest)}
                iconName="History"
                iconPosition="left"
              >
                Full History
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(guest)}
                className="text-error hover:text-error/80 ml-auto"
              >
                <Icon name="Trash2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestCard;